import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import '../../types/global.d.ts'

import { useQuery } from '@apollo/client/react'
import * as ReactDOM from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { initSentry } from './SentryClient'
import { FeaturesWrapper, useSplitEnabled } from '@buffer-mono/features'

import ModalManager from '../../components/ModalManager'
import { UserContext } from '../../common/context/User'

import { QUERY_ACCOUNT } from '../../common/graphql/account'
import trackUser from '../../common/utils/trackUser'
import { ORGANIZATION_ACTIONS } from '../../common/events/orgEvents'
import { ORCHESTRATOR_ACTIONS } from '../../common/events/orchestratorEvents'
import { MODAL_ACTIONS } from '../../common/events/modalEvents'
import { ACCOUNT_ACTIONS } from '../../common/events/accountEvents'
import { ENGAGE_ACTIONS } from '../../common/events/engageEvents'
import { MODALS } from '../../common/types'
import type { Account } from '../../common/types'
import ErrorBoundary from './ErrorBoundary'
import { PopcornProvider, useMediaQuery } from '@buffer-mono/popcorn'
import { getLoginUrl } from '@buffer-mono/shared-utils/urls'

import { isInPublish, redirectToUrl } from '../../common/utils/urls'

import store from '../Orchestrator/store/orchestratorStore'
import {
  AppshellEventKeys,
  OrganizationActionKeys,
} from '../../common/events/types'
import BannerContainer from './components/BannerContainer/BannerContainer'
import { Enforce2FARedirect } from './components/Enforce2FARedirect'
import APIGatewayProvider from './providers'
import useHelpScoutBeacon from '../../common/hooks/useHelpScoutBeacon'
import PreAuthenticationRedirect from '../Orchestrator/channelConnections/components/PreAuthenticationRedirect'
import {
  handleOrchestratorActionsOnload,
  handleOrchestratorHashChange,
} from './utils/handleOrchestratorActionsOnload'
import { handleModalEvent } from '../../components/ModalManager/ModalManagerUtils'
import { i18n } from '../../common/i18n/index.js'
import { I18nextProvider } from 'react-i18next'
import { HelpWidget } from '../../common/components/HelpWidget/HelpWidget.js'
import Navigation from '../../components/Navigation'
import { MEDIA_QUERIES } from '../../common/constants/mediaQueries.js'
import AutomaticStatusBanner from './components/AutomaticStatusBanner/AutomaticStatusBanner.js'

interface AppShellProps {
  hideNavigation: boolean
  hideBanners: boolean
}
export const AppShell = ({
  hideNavigation = false,
  hideBanners = false,
}: AppShellProps): JSX.Element => {
  return (
    <APIGatewayProvider>
      <FeaturesWrapper>
        <PopcornProvider
          theme="system"
          portalContainerId={'portals-container-app-shell'}
          // Disable toaster in Publish since the toaster in Publish will pick up the toast notifications
          // TODO: Remove this once AppShell is fully integrated with Publish
          _toaster={!isInPublish()}
        >
          <div
            id="portals-container-app-shell"
            style={{ zIndex: '9999' }}
          ></div>
          <ReduxProvider store={store}>
            <Navigator
              hideNavigation={hideNavigation}
              hideBanners={hideBanners}
            />
          </ReduxProvider>
        </PopcornProvider>
      </FeaturesWrapper>
    </APIGatewayProvider>
  )
}

export const ReduxWrappedNavigator = React.memo(
  ({
    hideNavigation = false,
    hideBanners = false,
  }: AppShellProps): JSX.Element => {
    return (
      <ReduxProvider store={store}>
        <Navigator hideNavigation={hideNavigation} hideBanners={hideBanners} />
      </ReduxProvider>
    )
  },
)
ReduxWrappedNavigator.displayName = 'ReduxWrappedNavigator'

const DUMMY_USER = {
  name: '...',
  email: '...',
  products: [],
  featureFlips: [],
  organizations: [],
  currentOrganization: {},
  isImpersonation: false,
  loading: true,
}
interface NavigatorProps {
  hideNavigation: boolean
  hideBanners: boolean
}
export const Navigator = React.memo(
  ({
    hideNavigation = false,
    hideBanners = false,
  }: NavigatorProps): JSX.Element => {
    const isMobile = useMediaQuery(`(width < ${MEDIA_QUERIES.MOBILE_LARGE}px)`)
    const shouldHideBrainfishWidget = isMobile

    const { data, loading, error, refetch } = useQuery(QUERY_ACCOUNT, {
      errorPolicy: 'all',
    })

    // Cold-load only: background refetches report `loading: true` while the
    // cached account keeps rendering — swapping in DUMMY_USER on every
    // refetch blanks navigation, banners, and org data across all host apps.
    const isColdLoading = loading && !data?.account

    window.__userData = {
      data,
      loading,
      error,
    }

    window.appshell = {
      eventKeys: { ...AppshellEventKeys },
      actions: {
        ...MODAL_ACTIONS,
        ...ORCHESTRATOR_ACTIONS,
        ...ORGANIZATION_ACTIONS,
        ...ACCOUNT_ACTIONS,
        ...ENGAGE_ACTIONS,
      },
      actionKeys: {
        ...OrganizationActionKeys,
      },
      MODALS,
      orchestratorStore: store,
    }

    const user: Account = data?.account ?? DUMMY_USER

    const userRef = React.useRef(user)
    userRef.current = user

    useEffect(() => {
      if (!isColdLoading && user.id) {
        handleOrchestratorActionsOnload(user)
        trackUser(user)
      }
    }, [user, isColdLoading])

    useEffect(() => {
      const onHashChange = (): void => {
        handleOrchestratorHashChange(userRef.current)
      }
      window.onhashchange = onHashChange
      return () => {
        window.onhashchange = null
      }
    }, [])

    useEffect(() => {
      if (error) {
        Sentry.captureException(error)
      }
    }, [error])

    const showBeaconInHelpMenu = useSplitEnabled('move-beacon-to-help-menu')
    const loadBeaconWidget = useSplitEnabled('beacon-web-widget')
    const loadBrainfishWidget = true

    const [isHelpButtonHidden, setIsHelpButtonHidden] = useState(false)

    useHelpScoutBeacon(
      user,
      showBeaconInHelpMenu,
      loadBeaconWidget,
      setIsHelpButtonHidden,
    )

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error (TS2339) Property 'result' does not exist on type 'Error | ServerParseError | ServerError'.
    const networkErrors = error?.networkError?.result?.errors
    if (
      networkErrors?.some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => err.extensions?.code === 'UNAUTHENTICATED',
      )
    ) {
      redirectToUrl(getLoginUrl(window.location.href))
    }

    // This function allows us to call a refetch on our GetAccount query
    // This was added to fix the data sync when
    // the ChannelConnectionUpgradeModal was being call by the account app
    // We needed to make sure we were getting the latest data and not looking in our cache
    const handleRefetching = useCallback<() => Promise<void>>(() => {
      return new Promise((resolve) => {
        refetch().then(() => {
          resolve()
        })
      })
    }, [refetch])

    useEffect(() => {
      window.addEventListener(AppshellEventKeys.MODAL_EVENT_KEY, (e) =>
        handleModalEvent(e, handleRefetching),
      )

      window.addEventListener(
        AppshellEventKeys.BILLING_EVENT_KEY,
        handleRefetching,
      )

      window.addEventListener(
        AppshellEventKeys.ACCOUNT_EVENT_KEY,
        handleRefetching,
      )

      return function cleanup(): void {
        window.removeEventListener(
          AppshellEventKeys.BILLING_EVENT_KEY,
          handleRefetching,
        )

        window.removeEventListener(
          AppshellEventKeys.ACCOUNT_EVENT_KEY,
          handleRefetching,
        )

        window.removeEventListener(AppshellEventKeys.MODAL_EVENT_KEY, (e) =>
          handleModalEvent(e, handleRefetching),
        )
      }
    }, [handleRefetching])

    return (
      <I18nextProvider i18n={i18n}>
        <AutomaticStatusBanner />
        <UserContext.Provider value={user}>
          {!isColdLoading && <Enforce2FARedirect />}
          {/* render all banners at the top for consistency */}
          {!isColdLoading && !hideBanners && <BannerContainer />}
          {user.id && <ModalManager />}
          {!hideNavigation && <Navigation />}
          {!hideNavigation && !shouldHideBrainfishWidget && (
            <HelpWidget
              beaconInHelpMenuFlag={showBeaconInHelpMenu}
              user={user}
              isButtonHidden={isHelpButtonHidden}
              onButtonVisibilityChange={setIsHelpButtonHidden}
            />
          )}
          <PreAuthenticationRedirect />
        </UserContext.Provider>
      </I18nextProvider>
    )
  },
)

Navigator.displayName = 'Navigator'

export default (): void => {
  const navigator = document.getElementById('navigator')
  const hideNavigation = !!navigator?.hasAttribute('data-hide-navigation')
  const hideBanners = !!navigator?.hasAttribute('data-hide-banners')
  if (navigator) {
    initSentry()
    ReactDOM.createRoot(navigator).render(
      <ErrorBoundary>
        <React.StrictMode>
          <AppShell hideNavigation={hideNavigation} hideBanners={hideBanners} />
        </React.StrictMode>
      </ErrorBoundary>,
    )
  }
}
